import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import { ProveedorSesion } from "./lib/sesion";
import PortalApp from "./portal/PortalApp";
import { ProveedorSesionPortal } from "./portal/sesionPortal";
import { idiomaActual, ProveedorIdioma, traducir } from "./lib/idioma";
import CartaApp from "./carta/CartaApp";
import PlataformaApp from "./plataforma/PlataformaApp";
import { ProveedorSesionPlataforma } from "./plataforma/sesionPlataforma";
import { registrarTrabajador } from "./portal/avisos";
import { establecerAmbito } from "./lib/ambito";
import "./estilos.css";

const cliente = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
  },
});

// Cuatro pantallas en el mismo sitio: la gestión del club, el portal del socio,
// la carta del local y el panel de quien vende el programa. Se elige por la
// dirección, y cada sesión guarda su token por separado.
const ruta = window.location.pathname;
const esPortal = ruta.startsWith("/portal");
/*
 * La barra final importa: la carta publica es siempre /carta/<club>/<token>, y
 * sin ella «/cartas» -la pantalla donde el club las administra- acababa
 * abriendo la carta del local en vez de la gestion.
 */
const esCarta = ruta.startsWith("/carta/");
const esPlataforma = ruta.startsWith("/plataforma");
establecerAmbito(esPortal ? "portal" : esPlataforma ? "plataforma" : "gestion");

// Cada pantalla dice en la pestana lo que es, que a menudo hay varias abiertas.
document.title = traducir(
  esCarta ? "titulo.carta" : esPlataforma ? "titulo.plataforma" : esPortal ? "titulo.portal" : "titulo.gestion",
);

// Solo el portal del socio se instala en el móvil: el manifiesto y el trabajador
// de segundo plano se enganchan ahí, no en la aplicación de gestión.
if (esPortal) {
  /*
   * Uno por idioma: el manifiesto lo descarga el navegador por su cuenta al
   * instalar el portal, asi que no se traduce en pantalla como lo demas.
   *
   * Va en el idioma de este movil, que es el unico que se sabe aqui: cuando
   * esto corre todavia no hay sesion, asi que el del socio no ha llegado. Es el
   * que vale, porque el rotulo debajo del icono lo lee quien lo instala.
   */
  const manifiesto = document.createElement("link");
  manifiesto.rel = "manifest";
  manifiesto.href = `/manifest-${idiomaActual()}.webmanifest`;
  document.head.appendChild(manifiesto);

  const iconoIos = document.createElement("link");
  iconoIos.rel = "apple-touch-icon";
  iconoIos.href = "/icono-192.png";
  document.head.appendChild(iconoIos);

  void registrarTrabajador();
} else if (!esCarta && !esPlataforma && "serviceWorker" in navigator && import.meta.env.PROD) {
  /*
   * La aplicacion de gestion guarda su armazon para abrirse sin red: es lo
   * que hace falta para que la tablet del mostrador recargue con la WiFi
   * caida y siga con su cola de entregas (ver public/sw-gestion.js). Solo en
   * produccion: en desarrollo el servidor de Vite sirve el codigo al vuelo y
   * un armazon guardado solo daria versiones viejas.
   */
  void navigator.serviceWorker.register("/sw-gestion.js", { scope: "/" }).catch(() => undefined);
}

ReactDOM.createRoot(document.getElementById("raiz")!).render(
  <React.StrictMode>
    <QueryClientProvider client={cliente}>
      {esCarta ? (
        /* La carta habla el idioma del club, que llega con los datos: el
           proveedor esta para que pueda aplicarlo. */
        <ProveedorIdioma>
          <CartaApp />
        </ProveedorIdioma>
      ) : esPlataforma ? (
        <ProveedorIdioma inicial="es">
          {/* El panel de plataforma es nuestro y va en castellano; el
              proveedor esta porque las piezas compartidas traducen. */}
          <ProveedorSesionPlataforma>
            <PlataformaApp />
          </ProveedorSesionPlataforma>
        </ProveedorIdioma>
      ) : esPortal ? (
        <ProveedorIdioma>
          <ProveedorSesionPortal>
            <PortalApp />
          </ProveedorSesionPortal>
        </ProveedorIdioma>
      ) : (
        <BrowserRouter>
          {/* La web publica (portada, precio, pedir una prueba) y el portal
              del socio van en castellano y en catalan; de las pantallas de
              gestion, las que mas se usan. El resto, poco a poco. */}
          <ProveedorIdioma>
            <ProveedorSesion>
              <App />
            </ProveedorSesion>
          </ProveedorIdioma>
        </BrowserRouter>
      )}
    </QueryClientProvider>
  </React.StrictMode>,
);
